import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AMPageShell from './components/AMPageShell';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';
import { ROUTES } from 'config/navConfig';

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TYPES = ['Quarterly Update', 'Annual Review', 'Watchlist Update', 'ESG Update', 'Ad Hoc'];
const PERIODS = ['Q1 2026', 'Q2 2026', 'Q3 2025', 'Full Year 2025'];
const TRIGGER_REASONS = ['Periodic review', 'Performance variance', 'Reserved matter', 'Counterparty issue', 'Distress signal'];
const OVERSIGHT_MEETINGS = ['15 Jan 2026', '05 Feb 2026', '05 Mar 2026'];

const GOV_KEYS = [
  { key: 'boardPapers',           label: 'Board Papers' },
  { key: 'financialModel',        label: 'Financial Model' },
  { key: 'financeReview',         label: 'Finance Review' },
  { key: 'hsePolicy',             label: 'HSE Policy' },
  { key: 'esgPolicy',             label: 'ESG Policy' },
  { key: 'annualValuationNotes',  label: 'Valuation Notes' },
];

const ACTION_STATUSES = ['in-progress', 'planned', 'complete', 'overdue'];
const STATUS_STYLES = {
  'in-progress': { bg: 'bg-primary/10',   text: 'text-primary'  },
  'planned':     { bg: 'bg-muted',        text: 'text-muted-foreground' },
  'complete':    { bg: 'bg-success/10',   text: 'text-success'  },
  'overdue':     { bg: 'bg-error/10',     text: 'text-error'    },
};

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepPills({ current, total }) {
  const STEP_LABELS = ['Cover', 'Operational Update', 'Actions & Governance', 'Review & Submit'];
  return (
    <div className="flex items-center gap-2 flex-wrap mb-6">
      {Array.from({ length: total }, (_, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={step}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-semibold transition-all ${
                active ? 'text-white' : done ? 'text-success' : 'text-muted-foreground'
              }`}
              style={{
                background: active ? 'var(--color-primary)' : done ? 'var(--color-success-muted, rgba(5,150,105,0.12))' : 'var(--color-muted)',
              }}
            >
              {done ? <Icon name="Check" size={12} color="var(--color-success)" /> : (
                <span className={active ? 'text-white' : 'text-muted-foreground'}>{step}</span>
              )}
              <span>{STEP_LABELS[i]}</span>
            </div>
            {step < total && (
              <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Label({ children }) {
  return (
    <label className="font-caption text-sm font-medium text-foreground block mb-1.5">{children}</label>
  );
}

function SelectField({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

function TextArea({ value, onChange, rows = 3, placeholder = '' }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none leading-relaxed"
    />
  );
}

// ─── Step 1 — Cover ───────────────────────────────────────────────────────────

function Step1({ form, setForm }) {
  const selectedAsset = ASSETS.find((a) => a.id === form.assetId) || ASSETS[0];
  const fundShort = selectedAsset?.fund?.replace('SeedDrive Infrastructure ', '').replace('Greenfield ', '') || 'Fund I';

  return (
    <div className="space-y-5">
      <div>
        <Label>Asset</Label>
        <select
          value={form.assetId}
          onChange={(e) => setForm((f) => ({ ...f, assetId: e.target.value }))}
          className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {ASSETS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: 'var(--color-primary)', color: '#fff' }}>
            <Icon name="Building2" size={11} color="#fff" />
            {fundShort}
          </span>
          <span className="font-caption text-xs text-muted-foreground">{selectedAsset?.sector} · {selectedAsset?.country}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Report Type</Label>
          <SelectField value={form.reportType} onChange={(v) => setForm((f) => ({ ...f, reportType: v }))} options={REPORT_TYPES} />
        </div>
        <div>
          <Label>Reporting Period</Label>
          <SelectField value={form.period} onChange={(v) => setForm((f) => ({ ...f, period: v }))} options={PERIODS} />
        </div>
        <div>
          <Label>Trigger Reason</Label>
          <SelectField value={form.triggerReason} onChange={(v) => setForm((f) => ({ ...f, triggerReason: v }))} options={TRIGGER_REASONS} />
        </div>
        <div>
          <Label>Target Oversight Meeting</Label>
          <SelectField value={form.targetMeeting} onChange={(v) => setForm((f) => ({ ...f, targetMeeting: v }))} options={OVERSIGHT_MEETINGS} />
        </div>
      </div>
    </div>
  );
}

// ─── Step 2 — Operational Update ─────────────────────────────────────────────

function Step2({ form, setForm }) {
  const asset = ASSETS.find((a) => a.id === form.assetId) || ASSETS[0];
  const riskIncreased = form.riskScore > (asset?.riskScore || 3);

  return (
    <div className="space-y-5">
      {/* Performance snapshot */}
      <div>
        <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Performance Snapshot — from current record
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Investment IRR', value: `${asset?.irr}%` },
            { label: 'Cash Yield (Actual)', value: `${asset?.cashYieldActual}%` },
            { label: 'Multiple', value: `${asset?.multiple}x` },
            { label: 'Prior Risk Score', value: `${asset?.riskScore} / 5` },
          ].map((m) => (
            <div key={m.label} className="bg-muted/40 rounded-lg p-3 border border-border">
              <p className="font-caption text-xs text-muted-foreground">{m.label}</p>
              <p className="font-data text-lg font-semibold text-foreground mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>Situation Update</Label>
        <TextArea
          value={form.situationUpdate}
          onChange={(v) => setForm((f) => ({ ...f, situationUpdate: v }))}
          rows={4}
          placeholder="Describe the current operational situation…"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Outperformance Drivers</Label>
          <TextArea
            value={form.outperformanceDrivers}
            onChange={(v) => setForm((f) => ({ ...f, outperformanceDrivers: v }))}
            rows={3}
            placeholder="Key drivers of outperformance…"
          />
        </div>
        <div>
          <Label>Underperformance Drivers</Label>
          <TextArea
            value={form.underperformanceDrivers}
            onChange={(v) => setForm((f) => ({ ...f, underperformanceDrivers: v }))}
            rows={3}
            placeholder="Key drivers of underperformance or risk…"
          />
        </div>
      </div>

      <div>
        <Label>Risk Assessment</Label>
        <div className="flex items-center gap-3">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              onClick={() => setForm((f) => ({ ...f, riskScore: score }))}
              className="w-10 h-10 rounded-lg border-2 font-data text-sm font-bold transition-all"
              style={{
                borderColor: form.riskScore === score ? 'var(--color-primary)' : 'var(--color-border)',
                background: form.riskScore === score ? 'var(--color-primary)' : 'transparent',
                color: form.riskScore === score ? '#fff' : 'var(--color-foreground)',
              }}
            >
              {score}
            </button>
          ))}
          <span className="font-caption text-xs text-muted-foreground ml-1">1 = Low · 5 = High</span>
        </div>
        {riskIncreased && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ background: 'rgba(217,119,6,0.08)', borderColor: 'rgba(217,119,6,0.3)' }}>
            <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-warning)' }}>
              Risk score increased — this flag will appear on Risk Monitor
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3 — Actions & Governance ───────────────────────────────────────────

function Step3({ form, setForm }) {
  const addAction = () => {
    setForm((f) => ({
      ...f,
      actions: [...f.actions, { id: `NEW-${Date.now()}`, action: '', owner: '', dueDate: '', status: 'planned' }],
    }));
  };

  const updateAction = (id, field, value) => {
    setForm((f) => ({
      ...f,
      actions: f.actions.map((a) => a.id === id ? { ...a, [field]: value } : a),
    }));
  };

  const toggleGov = (key) => {
    setForm((f) => ({ ...f, govChecks: { ...f.govChecks, [key]: !f.govChecks[key] } }));
  };

  return (
    <div className="space-y-6">
      {/* Agreed Actions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-caption text-sm font-semibold text-foreground">Agreed Actions</p>
          <button
            onClick={addAction}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border font-caption text-xs font-medium text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="Plus" size={13} color="var(--color-foreground)" />
            Add Action
          </button>
        </div>
        <div className="space-y-3">
          {form.actions.map((action) => {
            const style = STATUS_STYLES[action.status] || STATUS_STYLES['planned'];
            return (
              <div key={action.id} className="bg-card border border-border rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label>Action</Label>
                    <input
                      type="text"
                      value={action.action}
                      onChange={(e) => updateAction(action.id, 'action', e.target.value)}
                      placeholder="Describe the action…"
                      className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <Label>Owner</Label>
                    <input
                      type="text"
                      value={action.owner}
                      onChange={(e) => updateAction(action.id, 'owner', e.target.value)}
                      placeholder="Name / initials"
                      className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label>Due Date</Label>
                    <input
                      type="text"
                      value={action.dueDate}
                      onChange={(e) => updateAction(action.id, 'dueDate', e.target.value)}
                      placeholder="e.g. 30 Apr 2026"
                      className="border border-border rounded-lg px-3 py-1.5 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary w-36"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>Status</Label>
                    <select
                      value={action.status}
                      onChange={(e) => updateAction(action.id, 'status', e.target.value)}
                      className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {ACTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded font-caption text-xs font-semibold ${style.bg} ${style.text}`}>
                    {action.status}
                  </span>
                </div>
              </div>
            );
          })}
          {form.actions.length === 0 && (
            <p className="font-caption text-sm text-muted-foreground py-2">No actions — click "Add Action" to record one.</p>
          )}
        </div>
      </div>

      {/* Governance Checklist */}
      <div>
        <p className="font-caption text-sm font-semibold text-foreground mb-3">Governance Checklist</p>
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {GOV_KEYS.map(({ key, label }) => {
            const checked = form.govChecks[key];
            return (
              <div key={key} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleGov(key)}
                    className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      borderColor: checked ? 'var(--color-success)' : 'var(--color-border)',
                      background: checked ? 'var(--color-success)' : 'transparent',
                    }}
                  >
                    {checked && <Icon name="Check" size={11} color="#fff" />}
                  </button>
                  <span className="font-caption text-sm text-foreground">{label}</span>
                </div>
                {!checked && (
                  <span className="font-caption text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'rgba(217,119,6,0.1)', color: 'var(--color-warning)' }}>
                    Missing
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* LP Reporting Flag */}
      <div>
        <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setForm((f) => ({ ...f, lpFlag: !f.lpFlag }))}
              className="relative w-10 h-5 rounded-full transition-all flex-shrink-0"
              style={{ background: form.lpFlag ? 'var(--color-primary)' : 'var(--color-border)' }}
            >
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
                style={{ left: form.lpFlag ? '1.25rem' : '0.125rem' }}
              />
            </button>
            <div>
              <p className="font-caption text-sm font-medium text-foreground">Include in next LP periodic report (Fund I Q1 2026)</p>
              <p className="font-caption text-xs text-muted-foreground">Toggle on to feed this report into the Q1 LP update</p>
            </div>
          </div>
        </div>
        {form.lpFlag && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ background: 'rgba(59,76,76,0.06)', borderColor: 'var(--color-primary)' }}>
            <Icon name="Users" size={14} color="var(--color-primary)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-primary)' }}>
              This report will feed the Q1 2026 LP update for Fund I
            </span>
          </div>
        )}
      </div>

      {/* ESG Notes */}
      <div>
        <Label>ESG Notes (optional)</Label>
        <TextArea
          value={form.esgNotes}
          onChange={(v) => setForm((f) => ({ ...f, esgNotes: v }))}
          rows={3}
          placeholder="Note any ESG updates relevant to this reporting period…"
        />
      </div>
    </div>
  );
}

// ─── Step 4 — Review & Submit ─────────────────────────────────────────────────

function Step4({ form }) {
  const asset = ASSETS.find((a) => a.id === form.assetId) || ASSETS[0];
  const fundShort = asset?.fund?.replace('SeedDrive Infrastructure ', '').replace('Greenfield ', '') || 'Fund I';
  const govComplete = GOV_KEYS.filter(({ key }) => form.govChecks[key]).length;
  const govTotal = GOV_KEYS.length;

  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="FileText" size={16} color="var(--color-primary)" />
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>Report Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: 'Asset', value: asset?.name },
            { label: 'Fund', value: fundShort },
            { label: 'Report Type', value: form.reportType },
            { label: 'Period', value: form.period },
            { label: 'Trigger', value: form.triggerReason },
            { label: 'Target Meeting', value: form.targetMeeting },
          ].map((f) => (
            <div key={f.label} className="bg-muted/30 rounded-md p-3">
              <p className="font-caption text-xs text-muted-foreground">{f.label}</p>
              <p className="font-caption text-sm font-semibold text-foreground mt-0.5">{f.value}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 pt-1 border-t border-border flex-wrap">
          <span className="font-caption text-sm text-foreground">
            <span className="font-semibold">{form.actions.length}</span> agreed action{form.actions.length !== 1 ? 's' : ''}
          </span>
          <span className="font-caption text-sm text-foreground">
            Governance: <span className="font-semibold">{govComplete}/{govTotal}</span> items complete
          </span>
          {form.lpFlag && (
            <span className="flex items-center gap-1 font-caption text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'var(--color-primary)', color: '#fff' }}>
              <Icon name="Users" size={11} color="#fff" />
              LP Report Feed: On
            </span>
          )}
        </div>
      </div>

      {/* Sign-off chain preview */}
      {asset?.reviewChain?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="CheckSquare" size={15} color="var(--color-primary)" />
            <p className="font-caption text-sm font-semibold text-foreground">Committee Sign-off Chain</p>
          </div>
          <div className="space-y-2 mb-3">
            {asset.reviewChain.map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ background: step.signed ? '#059669' : 'var(--color-muted-foreground)' }}
                >
                  {step.signed ? <Icon name="Check" size={12} color="#fff" /> : step.initials}
                </div>
                <div className="flex-1">
                  <span className="font-caption text-sm font-semibold text-foreground">{step.name}</span>
                  <span className="font-caption text-xs text-muted-foreground ml-2">{step.role}</span>
                </div>
                <span className="font-caption text-xs font-semibold" style={{ color: step.signed ? '#059669' : '#d97706' }}>
                  {step.signed ? `Signed ${step.signedDate}` : 'Pending'}
                </span>
              </div>
            ))}
          </div>
          <p className="font-caption text-xs text-muted-foreground">
            Committee sign-off required before this report is finalised and distributed to LPs.
          </p>
        </div>
      )}

      {govComplete < govTotal && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border" style={{ background: 'rgba(217,119,6,0.08)', borderColor: 'rgba(217,119,6,0.3)' }}>
          <Icon name="AlertTriangle" size={15} color="var(--color-warning)" />
          <span className="font-caption text-sm" style={{ color: 'var(--color-warning)' }}>
            {govTotal - govComplete} governance item{govTotal - govComplete > 1 ? 's are' : ' is'} incomplete — you can still submit, but the committee may flag this.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function buildInitialForm(assetId) {
  const asset = ASSETS.find((a) => a.id === assetId) || ASSETS[0];
  const govChecks = {};
  GOV_KEYS.forEach(({ key }) => {
    const raw = asset?.governanceDocs?.[key];
    govChecks[key] = raw && raw !== 'Missing' && raw !== null && raw !== false;
  });
  return {
    assetId: asset.id,
    reportType: 'Quarterly Update',
    period: 'Q1 2026',
    triggerReason: 'Periodic review',
    targetMeeting: '15 Jan 2026',
    situationUpdate: asset?.situationUpdate || '',
    outperformanceDrivers: asset?.outperformanceDrivers || '',
    underperformanceDrivers: asset?.underperformanceDrivers || '',
    riskScore: asset?.riskScore || 3,
    actions: (asset?.agreedActionsList || []).map((a) => ({ ...a })),
    govChecks,
    lpFlag: true,
    esgNotes: '',
  };
}

export default function ReportBuilder({ showToast, setOversightQueue }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => buildInitialForm('AST-001'));

  // When asset changes in Step 1, re-initialise the downstream fields
  const handleAssetChange = (assetId) => {
    setForm(buildInitialForm(assetId));
  };

  // Override the asset dropdown's onChange to also re-init
  const formWithAssetHandler = {
    ...form,
    assetId: form.assetId,
  };

  const setFormWrapped = (updater) => {
    setForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.assetId !== prev.assetId) {
        return buildInitialForm(next.assetId);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const asset = ASSETS.find((a) => a.id === form.assetId) || ASSETS[0];
    const fundShort = asset?.fund?.replace('SeedDrive Infrastructure ', '').replace('Greenfield ', '') || 'Fund I';
    const newEntry = {
      id: `OQ-${Date.now()}`,
      asset: asset.name,
      triggerReason: form.triggerReason,
      reviewDate: form.targetMeeting,
      owner: 'P. Sharma',
      status: 'Submitted',
      packId: asset.id,
    };
    setOversightQueue?.((q) => [newEntry, ...q]);
    showToast?.(`Report submitted for committee review — ${asset.name} ${form.period} (${fundShort})`);
    navigate(ROUTES.oversightHub);
  };

  const TOTAL_STEPS = 4;

  return (
    <AMPageShell activeTab={ROUTES.reportBuilder}>
      <div className="max-w-3xl">
        {/* Page header */}
        <div className="mb-6">
          <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.35rem' }}>Report Builder</h2>
          <p className="font-caption text-sm text-muted-foreground mt-0.5">
            Create and submit an asset report for committee review
          </p>
        </div>

        <StepPills current={step} total={TOTAL_STEPS} />

        <div className="bg-card border border-border rounded-lg shadow-xs p-6">
          {step === 1 && <Step1 form={form} setForm={setFormWrapped} />}
          {step === 2 && <Step2 form={form} setForm={setFormWrapped} />}
          {step === 3 && <Step3 form={form} setForm={setFormWrapped} />}
          {step === 4 && <Step4 form={form} />}

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-border">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border font-caption text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeft" size={15} color="var(--color-foreground)" />
              Back
            </button>

            {step < TOTAL_STEPS ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'var(--color-primary)' }}
              >
                Next
                <Icon name="ChevronRight" size={15} color="#fff" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-5 py-2 rounded-lg font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="Send" size={15} color="#fff" />
                Submit for Committee Review
              </button>
            )}
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
